<script setup lang="ts">
import type AssetBooking from '@/classes/assetBooking'
import BookingDisplayListCard from '@/components/booking/BookingDisplayListCard.vue'
import TextBtn from '@/components/ui-elements/buttons/TextBtn.vue'
import { GettersTypes, type State } from '@/store/store_types'
import { sortBookingsByProximityToCurrentDate } from '@/utils/bookingsSortingUtils'
import { computed } from 'vue'
import { useStore } from 'vuex'

const store = useStore<State>()
const fetching = computed<boolean>(() => store.getters.fetching)
const upcomingBookings = computed<AssetBooking[]>(
  () => store.getters[GettersTypes.getUpcomingBookings],
)
const vehicleBookings = computed(() => {
  return upcomingBookings.value.filter((booking) => booking.asset_type === 'vehicle')
})
const filteredVehicleBookings = computed(() => {
  return sortBookingsByProximityToCurrentDate(vehicleBookings.value)
})
const chargerBookings = computed(() => {
  return upcomingBookings.value.filter((booking) => booking.asset_type === 'charger')
})
const filteredChargerBookings = computed(() => {
  return sortBookingsByProximityToCurrentDate(chargerBookings.value)
})
</script>

<template>
  <v-card flat class="bg-pwt_snow">
    <v-card-title class="d-flex align-center flex-nowrap">
      Charger bookings
      <v-spacer></v-spacer>
      <TextBtn> Sort by </TextBtn>
      <TextBtn> Filter </TextBtn>
    </v-card-title>
    <v-card-text class="mb-10">
      <v-text-field class="mb-5" placeholder="Search..." hide-details></v-text-field>
      <div v-if="fetching">
        <v-row no-gutters>
          <v-col
            cols="12"
            md="6"
            v-for="i in 2"
            :key="i"
            :class="i % 2 === 0 ? 'pr-0  pr-md-2' : 'pl-0  pl-mb-2'"
          >
            <v-skeleton-loader
              type="sentences, avatar"
              class="mb-3 d-flex align-center flex-nowrap"
            />
          </v-col>
        </v-row>
      </div>
      <span v-else-if="chargerBookings.length === 0"> You have no upcoming charger bookings. </span>
      <span v-else-if="filteredChargerBookings.length === 0">
        You have no upcoming charger bookings that match your filters and/or search criteria.
      </span>
      <div v-else>
        <v-row no-gutters>
          <v-col
            cols="12"
            md="6"
            v-for="(booking, index) in filteredChargerBookings"
            :class="index % 2 === 0 ? 'pr-0  pr-md-2' : 'pl-0  pl-mb-2'"
          >
            <BookingDisplayListCard :key="booking.local_id" :booking="booking" />
          </v-col>
        </v-row>
      </div>
    </v-card-text>
    <v-card-title class="d-flex align-center flex-nowrap">
      Vehicle bookings
      <v-spacer></v-spacer>
      <TextBtn> Sort by </TextBtn>
      <TextBtn> Filter </TextBtn>
    </v-card-title>
    <v-card-text>
      <v-text-field placeholder="Search..." hide-details class="mb-5"></v-text-field>
      <div v-if="fetching">
        <v-row no-gutters>
          <v-col
            cols="12"
            md="6"
            v-for="i in 2"
            :key="i"
            :class="i % 2 === 0 ? 'pr-0  pr-md-2' : 'pl-0  pl-mb-2'"
          >
            <v-skeleton-loader
              type="sentences, avatar"
              class="mb-3 d-flex align-center flex-nowrap"
            />
          </v-col>
        </v-row>
      </div>
      <span v-else-if="vehicleBookings.length === 0"> You have no upcoming vehicle bookings. </span>
      <span v-else-if="filteredVehicleBookings.length === 0">
        You have no upcoming vehicle bookings that match your filters and/or search criteria.
      </span>
      <div v-else>
        <v-row no-gutters>
          <v-col
            cols="12"
            md="6"
            v-for="(booking, index) in filteredVehicleBookings"
            :class="index % 2 === 0 ? 'pr-0  pr-md-2' : 'pl-0  pl-mb-2'"
          >
            <BookingDisplayListCard :key="booking.local_id" :booking="booking" />
          </v-col>
        </v-row>
      </div>
    </v-card-text>
  </v-card>
</template>
